import React, { useContext, useState } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { TagsContainer } from "../TagsContainer";
import ModalImageContatc from "./ModalImage";
import CopyToClipboard from "../CopyToClipboard";
import { FunnelSelect } from "./FunnelSelect";
import EditIcon from '@material-ui/icons/Edit';
import { LanguageDefault } from "../LangueDefault";
import ContactDrawerTailwick from "./ContactDrawerTailwick";
import ContactDrawerBookings from "./ContactDrawerBookings";
import ContactBlockButton from "./ContactBlockButton";
import { AuthContext } from "../../context/Auth/AuthContext";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		backgroundColor: theme.palette.background.default,
		color: theme.palette.text.primary,
		borderTop: `1px solid ${theme.palette.divider}`,
		borderRight: `1px solid ${theme.palette.divider}`,
		borderBottom: `1px solid ${theme.palette.divider}`,
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "space-between",
		padding: "0 1.5rem"
	},
	content: {
		display: "flex",
		backgroundColor: theme.palette.background.default,
		flexDirection: "column",
		padding: "8px 0px 8px 8px",
		height: "100%",
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 15,
		width: 160,
		height: 160,
		borderRadius: 10,
	},

	contactHeader: {
		display: "flex",
		padding: 8,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.background.default,
		border: "none",
		"& > *": {
			margin: 4,
		},
	},

	contactNumber: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		cursor: "pointer",

		"&:hover $copyIcon": {
			opacity: 1,
			visibility: "visible",
		},
	},

	copyIcon: {
		opacity: 0,
		visibility: "hidden",
		transition: "opacity 0.2s ease",
	},

	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		borderRadius: 10,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.06)"
				: "#ffffff",
		color: theme.palette.text.primary,
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(255, 255, 255, 0.04)"
				: theme.palette.background.paper,
	},
}));

function ContactDrawer({ open, handleDrawerClose, contact, loading, tailwickLayout = false, ticketId }) {
	const classes = useStyles();
	const theme = useTheme();
	const { user } = useContext(AuthContext);
	const [modalOpen, setModalOpen] = useState(false);
	if (tailwickLayout) {
		return (
			<ContactDrawerTailwick
				open={open}
				handleDrawerClose={handleDrawerClose}
				contact={contact}
				loading={loading}
				user={user}
				ticketId={ticketId}
			/>
		);
	}

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute", width: "390px" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>

				<Typography style={{ justifySelf: "center", fontWeight: "bolder" }}>
					{i18n.t("contactDrawer.header")}

				</Typography>
				<IconButton color="primary" onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<ModalImageContatc
							imageUrl={contact.profilePicUrl}
							contactId={contact.id}
							channel={contact.channel}
						/>
						<Typography className={classes.contactNumber} style={{ marginLeft: 30, fontWeight: "bolder", display: "flex", alignItems: "center", justifyContent: "center" }} variant="h6">
							{contact.name}
							<span className={classes.copyIcon}>
								<CopyToClipboard content={contact.name} color="secondary" />
							</span>
						</Typography>
						{(user.userSeeNumbers || user.profile === "admin") ? (
							<Typography
								className={classes.contactNumber}
								style={{
									marginLeft: 30,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
								color="secondary"
							>
								<SmartphoneIcon />
								{contact.number}
								<span className={classes.copyIcon}>
									<CopyToClipboard content={contact.number} color="secondary" />
								</span>
							</Typography>
						) : (
							<Typography
								className={classes.contactNumber}
								style={{
									marginLeft: 30,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
								color="error"
							>
								<SmartphoneIcon />
								Você não tem permissão para visualizar este número.
							</Typography>
						)}
						{contact.email && (
							<Typography>
								<Link href={`mailto:${contact.email}`}>{contact.email}</Link>
								<CopyToClipboard content={contact.email} color="secondary" />
							</Typography>
						)}
						<Button
							style={{ margin: "20px 0px", padding: "0.3rem 5rem", borderRadius: 10 }}
							variant="outlined"
							onClick={() => setModalOpen(true)}
						>
							<EditIcon style={{ marginRight: 8 }} /> {i18n.t("contactDrawer.buttons.edit")}
						</Button>
						<ContactBlockButton contact={contact} ticketId={ticketId} />

					</Paper>
					<TagsContainer contact={contact} />
					<FunnelSelect contact={contact} />

					<LanguageDefault contact={contact} />
					<div style={{ padding: "8px 10px" }}>
						<ContactDrawerBookings contact={contact} ticketId={ticketId} />
					</div>

					<div
						style={{
							padding: "25px 10px",
							borderTop: `1px solid ${theme.palette.divider}`,
						}}
					>

						<Paper square variant="outlined" className={classes.contactDetails}>
							<ContactModal
								open={modalOpen}
								onClose={() => setModalOpen(false)}
								contactId={contact.id}
							/>
							<Typography variant="subtitle1">
								{i18n.t("contactDrawer.extraInfo")}
							</Typography>
							{contact?.extraInfo?.map((info) => (
								<Paper
									key={info.id}
									square
									variant="outlined"
									className={classes.contactExtraInfo}
								>
									<InputLabel>
										{info.name}
										<CopyToClipboard content={info.value} color="secondary" />
									</InputLabel>
									<Typography component="div" noWrap style={{ paddingTop: 2 }}>
										<MarkdownWrapper>{info.value}</MarkdownWrapper>
									</Typography>
								</Paper>
							))}
						</Paper>
					</div>

				</div>
			)}
		</Drawer>
	);
}

export default ContactDrawer;
